/**
 * SangeetNWedding Component
 * 
 * Displays both Sangeet and Wedding events together.
 * Shows timeline of Sangeet celebrations and wedding day ceremonies.
 */

import React from 'react';
import '../App.css';

// Centralized imports
import { useAuth, AuthGuard } from '../utils/auth';
import { useScrollWobble } from '../utils/scrollAnimations';
import { PAGE_EVENTS, VENUE_INFO } from '../data/eventsData';
import { generateHeroDateFromEvents } from '../utils/dateFormatting';
import EventCard from '../components/EventCard';
import ClosingSection from '../components/shared/ClosingSection';

function SangeetNWedding() {
  // Use centralized authentication hook
  const { isValidAccess, isLoading } = useAuth('/swaendgdeientg');
  
  // Use centralized scroll animation hook
  useScrollWobble();

  // Get sangeet and wedding events from centralized data
  const events = PAGE_EVENTS.sangeetNWedding();
  
  // Generate dynamic hero date from the actual events
  const heroDate = generateHeroDateFromEvents(events);
  
  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    const date = event.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});
  
  // Sort dates to ensure proper order
  const sortedDates = Object.keys(eventsByDate).sort((a, b) => {
    // Parse dates for proper sorting
    const dateA = new Date(a.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    const dateB = new Date(b.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    return dateA - dateB;
  });

  return (
    <AuthGuard isLoading={isLoading} isValidAccess={isValidAccess}>
      <div className="app">
        {/* Hero Section */}
        <div className="hero">
          <div className="hero-text">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>{heroDate}</p>
          </div>
        </div>

        {/* Hero Text Section for Mobile */}
        <div className="hero-text-section">
          <div className="hero-text-content">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>{heroDate}</p>
          </div>
        </div>

        <div className="gradient-container">
          {/* Location Section */}
          <div className="location-section">
          <div className="location-container">
            <div className="location-title">
              <img src="/location.png" alt="Location" className="location-icon" />
              <h2>Venue</h2>
            </div>
            <div className="location-details">
              <h3>{VENUE_INFO.name}</h3>
              <div className="location-address">
                {VENUE_INFO.address.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sangeet & Wedding Events Section */}
        <div className="agenda">
          {/* Events grouped by date */}
          {sortedDates.map((date, dateIndex) => (
            <div key={date}>
              {/* Date Header */}
              <div className="day-section">
                <div className="day-header">
                  <h2>{date.includes('24th') ? 'Sangeet Celebrations' : 'Wedding Ceremonies'}</h2>
                  <div className="date-ornament">
                    <span className="ornament">{date.includes('24th') ? '✨' : '🌹'}</span>
                    <span className="date">{date}</span>
                    <span className="ornament">{date.includes('24th') ? '✨' : '🌹'}</span>
                  </div>
                </div>
              </div>

              {/* Event Cards for this date */}
              {eventsByDate[date].map((event, eventIndex) => (
                <EventCard 
                  key={event.id} 
                  event={event} 
                  isReversed={eventIndex % 2 === 1}
                />
              ))}
            </div>
          ))}
        </div>

          {/* Closing Section */}
          <ClosingSection variant="simple" />
        </div>
      </div>
    </AuthGuard>
  );
}

export default SangeetNWedding;